import "./CdqResponseHistoryModal.css";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicianResponse } from "../CoderView/redux/CoderScreenSlice";
import jsPDF from "jspdf"; 
import autoTable from "jspdf-autotable"; 
/**
 * Reusable CDQ Response History slide-in drawer, with server-side pagination.
 *
 * Matches the /get_cdq_history_response API response shape:
 * {
 *   "status": "success",
 *   "statusCode": 200,
 *   "hcp_line_id": 104012,
 *   "episode_identifier": "SDX3FSEP",
 *   "count_cdq_response": 1,
 *   "page_number": 1,
 *   "page_size": 4,
 *   "cdq_responses": [
 *     {
 *       "cdq_assignment_id": 17,
 *       "clinician": "Dr.Anjali",
 *       "coder": "Sai",
 *       "cdq_responded_date": "13/08/2026 15:58:000000"
 *     }
 *   ]
 * }
 *
 * Props:
 * - show: boolean
 * - onClose: fn
 * - episodeIdentifier: string|number
 * - responses: array of raw cdq_responses items for the CURRENT page only
 * - totalCount: number - `count_cdq_response` from the API
 * - currentPage: number - 1-based current page (defaults to 1)
 * - pageSize: number - items per page (defaults to 4)
 * - loading: boolean - true while a page is being fetched
 * - error: any - truthy when the fetch failed
 * - onPageChange: fn(nextPage) - called when the user navigates pages
 */

// API sends dates like "13/08/2026 15:58:000000" (DD/MM/YYYY HH:mm:extra)
const parseCdqDate = (raw) => {
  if (!raw) return null;
  const [datePart, timePart] = raw.split(" ");
  if (!datePart) return null;
  const [day, month, year] = datePart.split("/").map(Number);
  let hours = 0;
  let minutes = 0;
  if (timePart) {
    const [h, m] = timePart.split(":");
    hours = Number(h) || 0;
    minutes = Number(m) || 0;
  }
  if (!day || !month || !year) return null;
  const d = new Date(year, month - 1, day, hours, minutes);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatCdqDate = (date) => {
  if (!date) return "-";
  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} · ${timePart}`;
};

const CdqResponseHistoryModal = ({
  show,
  onClose,
  episodeIdentifier,
  responses = [],
  totalCount,
  currentPage = 1,
  pageSize = 3,
  loading = false,
  error = null,
  onPageChange,
}) => {
  const dispatch = useDispatch();

const [loadingPdfId, setLoadingPdfId] = useState(null);
  useEffect(() => {
    if (show) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [show]);

  if (!show) return null;

  // Normalize the current page's rows and sort newest-first within the page
  const normalized = responses.map((r, idx) => {
    const parsedDate = parseCdqDate(r.cdq_responded_date);
   return {
  key: r.cdq_assignment_id ?? idx,
  cdqAssignmentId: r.cdq_assignment_id,
  coder: r.coder ?? "-",
  clinician: r.clinician ?? "-",
  dateLabel: formatCdqDate(parsedDate),
  sortTime: parsedDate ? parsedDate.getTime() : -idx,
};
  });

  const sorted = [...normalized].sort((a, b) => b.sortTime - a.sortTime);

  const displayCount = totalCount ?? sorted.length;
  const totalPages =
    typeof totalCount === "number" && pageSize > 0
      ? Math.max(1, Math.ceil(totalCount / pageSize))
      : 1;
  const isInitialLoading = loading && sorted.length === 0;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage || loading) {
      return;
    }
    onPageChange?.(page);
  };
  const handleViewPdf = async (cdqAssignmentId) => {
  // Open window immediately so browser does not block popup
  const pdfWindow = window.open("", "_blank");

  try {
    setLoadingPdfId(cdqAssignmentId);

    // Loading screen while API is being fetched
    if (pdfWindow) {
      pdfWindow.document.write(`
        <html>
          <head>
            <title>Clinical Documentation Query</title>
          </head>
          <body
            style="
              margin:0;
              display:flex;
              align-items:center;
              justify-content:center;
              height:100vh;
              font-family:Arial,sans-serif;
              color:#344054;
            "
          >
            Generating PDF...
          </body>
        </html>
      `);

      pdfWindow.document.close();
    }

    // ==========================================
    // FETCH CLINICIAN RESPONSE
    // ==========================================

    const result = await dispatch(
      fetchClinicianResponse({
        cdq_assignment_id: cdqAssignmentId,
      })
    ).unwrap();

    console.log("Clinician response:", result);

    // ==========================================
    // PDF SETUP
    // ==========================================

    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth =
      pdf.internal.pageSize.getWidth();

    const margin = 20;

    const contentWidth =
      pageWidth - margin * 2;

    // ==========================================
    // COLORS
    // ==========================================

    const COLORS = {
      primary: [0, 72, 129],
      secondary: [25, 190, 160],
      headerText: [255, 255, 255],
      text: [55, 65, 75],
      label: [71, 84, 103],
      border: [208, 213, 221],
      lightBg: [247, 249, 252],
      selectedBg: [232, 247, 243],
      gray: [100, 100, 100],
    };

    let y = 18;

    // ==========================================
    // HEADER
    // ==========================================

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.setTextColor(...COLORS.primary);

    pdf.text(
      "Clinical Documentation Query",
      pageWidth / 2,
      y,
      {
        align: "center",
      }
    );

    y += 9;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(...COLORS.gray);

    pdf.text(
      result.template_name ||
        "Clinical Documentation Query",
      pageWidth / 2,
      y,
      {
        align: "center",
      }
    );

    y += 16;

    // ==========================================
    // TEMPLATE
    // ==========================================

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);

    pdf.text(
      "Template",
      margin,
      y
    );

    y += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...COLORS.text);

    const templateLines =
      pdf.splitTextToSize(
        result.template_name || "-",
        contentWidth
      );

    pdf.text(
      templateLines,
      margin,
      y
    );

    y +=
      templateLines.length * 5 +
      12;

    // ==========================================
    // QUERY
    // ==========================================

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);

    pdf.text(
      "Query",
      margin,
      y
    );

    y += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...COLORS.text);

    const queryLines =
      pdf.splitTextToSize(
        result.query || "-",
        contentWidth
      );

    pdf.text(
      queryLines,
      margin,
      y
    );

    y +=
      queryLines.length * 5 +
      12;

    // ==========================================
    // FREE TEXT
    // ==========================================

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);

    pdf.text(
      "Free Text",
      margin,
      y
    );

    y += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...COLORS.text);

    const freeTextLines =
      pdf.splitTextToSize(
        result.free_text || "-",
        contentWidth
      );

    pdf.text(
      freeTextLines,
      margin,
      y
    );

    y +=
      freeTextLines.length * 5 +
      14;

    // ==========================================
    // RESPONSE OPTIONS
    // ==========================================

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);

    pdf.text(
      "Response Options",
      margin,
      y
    );

    y += 6;

    const responseOptions =
      result.response_options || [];

    autoTable(pdf, {
      startY: y,

      margin: {
        left: margin,
        right: margin,
      },

      theme: "grid",

      head: [
        [
          "Response Option",
          "Selected",
        ],
      ],

      body: responseOptions.map(
        (option) => [
          option.display_name ||
            option.option ||
            "-",

          option.is_selected?
            "CHECKED" : ""
        ]
      ),

      styles: {
        font: "helvetica",
        fontSize: 10.5,
        textColor: COLORS.text,

        lineColor: COLORS.border,
        lineWidth: 0.25,

        cellPadding: 3.5,
        valign: "middle",
      },

      headStyles: {
        fillColor: COLORS.secondary,
        textColor: COLORS.headerText,

        fontStyle: "bold",
        fontSize: 10.5,

        cellPadding: 3.5,
      },

      bodyStyles: {
        fillColor: [255, 255, 255],
      },

      alternateRowStyles: {
        fillColor: COLORS.lightBg,
      },

      columnStyles: {
        0: {
          cellWidth:
            contentWidth - 42,
          halign: "left",
        },

        1: {
          cellWidth: 42,
          halign: "center",
          fontStyle: "bold",
        },
      },

      didParseCell: (data) => {
        if (
          data.section === "body" &&
          data.column.index === 1 &&
          data.cell.raw === "CHECKED"
        ) {
          data.cell.styles.textColor =
            COLORS.secondary;

          data.cell.styles.fontStyle =
            "bold";

          data.cell.styles.fontSize = 13;

          data.cell.styles.fillColor =
            COLORS.selectedBg;
        }
      },
    });

    y =
      pdf.lastAutoTable.finalY +
      15;

    // ==========================================
    // RESPONDING CLINICIAN
    // ==========================================

    const clinicianInfo =
      result.responding_clinician_info || {};

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);

    pdf.text(
      "Responding Clinician",
      margin,
      y
    );

    y += 6;

    autoTable(pdf, {
      startY: y,

      margin: {
        left: margin,
        right: margin,
      },

      theme: "grid",

      head: [
        [
          "Field",
          "Value",
        ],
      ],

      body: [
        [
          "Clinician",
          clinicianInfo.responding_clinician ||
            "-",
        ],

        [
          "Specialty",
          clinicianInfo.specialty ||
            "-",
        ],

        [
          "Response Date",
          clinicianInfo.response_date ||
            "-",
        ],

        [
          "Response Time",
          clinicianInfo.response_time ||
            "-",
        ],
      ],

      styles: {
        font: "helvetica",
        fontSize: 10.5,
        textColor: COLORS.text,

        lineColor: COLORS.border,
        lineWidth: 0.25,

        cellPadding: 3.5,
        valign: "middle",
      },

      headStyles: {
        fillColor: COLORS.secondary,
        textColor: COLORS.headerText,

        fontStyle: "bold",
        fontSize: 10.5,

        cellPadding: 3.5,
      },

      bodyStyles: {
        fillColor: [255, 255, 255],
      },

      alternateRowStyles: {
        fillColor: COLORS.lightBg,
      },

      columnStyles: {
        0: {
          cellWidth: 75,
          fontStyle: "bold",
          textColor: COLORS.label,
        },

        1: {
          cellWidth:
            contentWidth - 75,
        },
      },
    });

    // ==========================================
    // CREATE PDF
    // ==========================================

    const pdfBlob =
      pdf.output("blob");

    const pdfUrl =
      URL.createObjectURL(pdfBlob);

    // ==========================================
    // OPEN PDF
    // ==========================================

    if (
      pdfWindow &&
      !pdfWindow.closed
    ) {
      pdfWindow.location.href =
        pdfUrl;
    } else {
      window.open(
        pdfUrl,
        "_blank"
      );
    }

    // Release object URL later
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 60000);

  } catch (error) {
    console.error(
      "Failed to fetch clinician response:",
      error
    );

    if (
      pdfWindow &&
      !pdfWindow.closed
    ) {
      pdfWindow.document.body.innerHTML = `
        <div
          style="
            font-family: Arial, sans-serif;
            padding: 40px;
            text-align: center;
            color: #D92D20;
          "
        >
          Failed to generate PDF.
        </div>
      `;
    }
  } finally {
    setLoadingPdfId(null);
  }
};
  return (
    <div className="cdq-modal-overlay" onClick={handleBackdropClick}>
      <div
        className="cdq-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cdq-modal-title"
      >
        <div className="cdq-modal-header">
          <div className="cdq-modal-header-text">
            <h5 id="cdq-modal-title" className="cdq-modal-title">
              CDQ Response History
            </h5>
            <p className="cdq-modal-subtitle">
              Episode ID: {episodeIdentifier ?? "-"}
              {!isInitialLoading && (
                <>
                  <span className="cdq-modal-subtitle-dot">·</span>
                  {displayCount} response{displayCount === 1 ? "" : "s"} received
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            className="cdq-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="cdq-modal-body">
          {isInitialLoading ? (
            <div className="cdq-modal-loading">
              <div className="cdq-modal-spinner" />
              <span>Loading CDQ responses…</span>
            </div>
          ) : error && sorted.length === 0 ? (
            <div className="cdq-modal-error">
              Unable to load CDQ responses. Please try again.
            </div>
          ) : sorted.length === 0 ? (
            <div className="cdq-modal-empty">
              No CDQ responses found for this episode.
            </div>
          ) : (
            <>
              {loading && (
                <div className="cdq-modal-loading-more">
                  <div className="cdq-modal-spinner cdq-modal-spinner-sm" />
                  Loading…
                </div>
              )}
              {!loading &&
                sorted.map((response, idx) => {
                  // Highest response number = most recent overall.
                  const offset = (currentPage - 1) * pageSize;
                  const responseNumber =
                    (totalCount ?? sorted.length) - offset - idx;
                  const isLatestOverall = currentPage === 1 && idx === 0;
                  return (
                    <div key={response.key} className="cdq-response-card">
                      <div className="cdq-response-card-top">
                        <span className="cdq-response-card-title">
                          Response {responseNumber}
                        </span>
                        {isLatestOverall && (
                          <span className="cdq-response-latest-badge">
                            LATEST
                          </span>
                        )}
                      </div>

                      <div className="cdq-response-card-row">
                        <span className="cdq-response-card-label">
                          Coder:
                        </span>
                        <span className="cdq-response-card-value">
                          {response.coder}
                        </span>
                      </div>
                      <div className="cdq-response-card-row">
                        <span className="cdq-response-card-label">
                          Clinician:
                        </span>
                        <span className="cdq-response-card-value">
                          {response.clinician}
                        </span>
                      </div>

                      <div className="cdq-response-card-date">
                        {response.dateLabel}
                      </div>

                     <div className="cdq-response-card-footer">
       <button
  type="button"
  className="cdq-response-view-pdf"
  onClick={() => handleViewPdf(response.cdqAssignmentId)}
  disabled={
    !response.cdqAssignmentId ||
    loadingPdfId === response.cdqAssignmentId
  }
>
  {loadingPdfId === response.cdqAssignmentId ? (
    <>
      <div className="cdq-modal-spinner cdq-modal-spinner-sm" />
      Loading...
    </>
  ) : (
    <>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />

        <path
          d="M14 2V8H20"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />

        <path
          d="M7 15H17"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />

        <text
          x="7"
          y="13"
          fontSize="4.5"
          fontWeight="700"
          fill="currentColor"
        >
          PDF
        </text>
      </svg>

      View PDF
    </>
  )}
</button>
</div>
                    </div>
                  );
                })}

              {error && !loading && (
                <div className="cdq-modal-error cdq-modal-error-inline">
                  Couldn't load this page. Please try again.
                </div>
              )}
            </>
          )}
        </div>

        {!isInitialLoading && sorted.length > 0 && totalPages > 1 && (
          <div className="cdq-modal-footer">
            <button
              type="button"
              className="cdq-page-btn"
              onClick={() => goToPage(1)}
              disabled={currentPage <= 1 || loading}
              aria-label="First page"
            >
              «
            </button>
            <button
              type="button"
              className="cdq-page-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="cdq-page-label">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="cdq-page-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              aria-label="Next page"
            >
              ›
            </button>
            <button
              type="button"
              className="cdq-page-btn"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage >= totalPages || loading}
              aria-label="Last page"
            >
              »
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CdqResponseHistoryModal;